import { Component, ElementRef, HostListener, inject, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/services/admin/admin.service';
import { LanguagesService } from 'src/app/services/languages/languages.service';
import { SmsService } from 'src/app/services/sms/sms.service';
import { index } from 'src/app/models/index';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderAuthComponent } from 'src/app/components/layout/header-auth/header-auth.component';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    CommonModule,
    HeaderAuthComponent,
    RouterModule
  ],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.css']
})
export class SignUpComponent implements OnInit {

  loading = false;
  validateForm!: FormGroup;
  error: any;
  passwordErrors: any
  data: any[] = [];

  // პაროლის პროგრესი
  passwordStrength = 0;
  isTyping = false;
  selectedLanguage: string = ''

  constructor(
    private fb: FormBuilder,
    public AuthService: AdminService,
    private smsService: SmsService,
    private translate: TranslateService,
    private languageService: LanguagesService,
    private Router: Router
  ) { 
    if(this.AuthService.isAdmin()){
      this.Router.navigate([''])
    }

    this.languageService.selectedLanguage$.subscribe(
      (lang: any) => {this.selectedLanguage = lang?.code}
    );

  }

  ngOnInit(): void {

    this.validateForm = this.fb.group({
      name: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      phone: [null],
      index: [null],
      email: ['', Validators.required],
      // email: ['', [Validators.required], [this.emailValidator]]

      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/.*[a-z].*/), // მინიმუმ 1 პატარა ასო
        // Validators.pattern(/.*[A-Z].*/), // მინიმუმ 1 დიდი ასო
        Validators.pattern(/.*\d.*/)    // მინიმუმ 1 ციფრი
      ]],
      check: [false, Validators.required],
      code: [null],
    });

     // ცვლილების მონიტორინგი პაროლის პროგრესის
    this.validateForm.get('password')?.valueChanges.subscribe(value => {
      this.isTyping = value.length > 0;
      this.calculateStrength(value);
    });


  }

  calculateStrength(password: string) {
    let strength = 0;
    if (password.length >= 8) strength++; // სიგრძის შემოწმება
    if (/[a-z]/.test(password)) strength++; // პატარა ასო
    if (/[A-Z]/.test(password)) strength++; // დიდი ასო
    if (/\d/.test(password)) strength++; // ციფრი
    if (/[\W_]/.test(password)) strength++; // სპეციალური სიმბოლო
    
    this.passwordStrength = (strength / 5) * 100; // 5 კრიტერიუმიდან პროცენტულად გამოთვლა
  }

  get passwordError() {
    const errors = this.validateForm.get('password')?.errors;
    if (!errors) return [];
    const errorMessages = [];
  
    if (errors['required']) errorMessages.push('validation.PASSWORD_REQUIRED');
    if (errors['minlength']) errorMessages.push('validation.PASSWORD_MIN');
    if (errors['pattern']) {
      const value = this.validateForm.get('password')?.value || '';
      if (!/.*[a-z].*/.test(value)) errorMessages.push('validation.PASSWORD_LOWERCASE');
      // if (!/.*[A-Z].*/.test(value)) errorMessages.push('validation.PASSWORD_UPPERCASE');
      if (!/.*\d.*/.test(value)) errorMessages.push('validation.PASSWORD_NUMBER');
      // if (!/.*[!@#$%^&*(),.?":{}|<>].*/.test(value)) errorMessages.push('validation.PASSWORD_SPECIAL');
    }
    return errorMessages;
  }

  showRules() {
    return true;
  }

  submitForm(): void {

    this.loading = true

    for (const i in this.validateForm.controls) {
      if (this.validateForm.controls.hasOwnProperty(i)) {
        this.validateForm.controls[i].markAsDirty();
        this.validateForm.controls[i].updateValueAndValidity();
      }
    }

    if (this.validateForm.valid) {

      const data = {
        name: this.validateForm.value.name,
        lastname: this.validateForm.value.lastname,
        phone_index: this.validateForm.value.index,
        phone: this.validateForm.value.phone,
        email: this.validateForm.value.email,
        password: this.validateForm.value.password,
        check: this.validateForm.value.check,
        lang: this.selectedLanguage
      };


      this.AuthService.signUp(data).subscribe(
        (res: any) => {
          console.log(res)
          this.loading = false
          if(res?.token){
            this.AuthService.setTokens(res?.token);
          }
          this.Router.navigate([], { queryParams: { checkverify: 'true' }, queryParamsHandling: 'merge' });
          // console.log(res)
          // this.startTimer();
        },
        (error: any) => {

          console.log('შეცდომა: ' ,error)  

          this.loading = false
          this.error = error?.error?.errors
          // გამოვყოფთ პაროლის შეცდომებს ცალკეულ ნაწილებად
          const password = this.validateForm.get('password')?.value;
          if (this.error.password && this.error.password[0]) {
            // გაყოფა ცარიელ სივრცეზე (space)
      
            this.passwordErrors = this.error.password[0].split(' ').filter((error: string) => {
              // თუ მომხმარებელმა დააკმაყოფილა მოთხოვნა, ამოშლით მას შეცდომების სიიდან
              if (error === 'validation.PASSWORD_LOWERCASE' && this.passwordContainsLowercase(password)) {
                return false;
              }
              // if (error === 'validation.PASSWORD_UPPERCASE' && this.passwordContainsUppercase(password)) {
              //   return false;
              // }
              if (error === 'validation.PASSWORD_NUMBER' && this.passwordContainsNumber(password)) {
                return false;
              }
              // if (error === 'validation.PASSWORD_SPECIAL' && this.passwordContainsSpecialCharacter(password)) {
              //   return false;
              // }
              return true; // მხოლოდ ის შეცდომები რჩება, რომლებიც რეალურად არ არის შესრულებული
            });


          } else {
            this.passwordErrors = [];
          }
        }
      );
    }
  }

  passwordContainsLowercase(password: string): boolean {
    return /[a-z]/.test(password);
  }
  
  // passwordContainsUppercase(password: string): boolean {
  //   return /[A-Z]/.test(password);
  // }
  
  passwordContainsNumber(password: string): boolean {
    return /[0-9]/.test(password);
  }


}